import React from 'react';
import { Paper, Typography, Box, Chip, Button, IconButton } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bolt as BoltIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Map as MapIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
// Import connector mappings from the enhanced socket types
import { getConnectorInfo } from './utils/socketTypes';
import { extractConnectorPower, getPowerDisplayInfo, debugChargerPowerData, normalizeConnector } from './utils/powerUtils';
import { getChargerAvailability } from './utils/availability';
import { getNetworkIconPath, getNetworkName } from './utils/markerIcons';

const ChargerHoverCard = ({ charger, position, onViewOnMap, onToggleFavorite, isFavorite = false, onAddToTrip, onRemoveFromTrip, isInRoute = false, isRouting = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardSurface = isDark ? alpha(theme.palette.background.paper, 0.95) : 'rgba(255, 255, 255, 0.95)';
  const cardBorder = isDark ? `1px solid ${alpha(theme.palette.common.white, 0.12)}` : '1px solid rgba(255, 255, 255, 0.2)';
  const metaSurface = isDark ? alpha(theme.palette.common.white, 0.06) : 'rgba(0, 0, 0, 0.02)';
  const connectorBg = isDark
    ? alpha(theme.palette.primary.light || theme.palette.primary.main, 0.35)
    : '#e3f2fd';
  const connectorBorderColor = isDark
    ? alpha(theme.palette.primary.light || theme.palette.primary.main, 0.8)
    : theme.palette.primary.main;
  const viewButtonGradient = 'linear-gradient(120deg, #5C7CFF 0%, #7D65FF 50%, #B64BFF 100%)';
  // Helper to count station/connector items
  const getStationCount = (c) => {
    if (!c) return 0;
    const webappStations = c?.webappData?.[0]?.devices?.flatMap(d => d.stations || []) || [];
    if (webappStations.length) return webappStations.length;
    const deviceStations = c?.devices?.flatMap(d => d.stations || []) || [];
    if (deviceStations.length) return deviceStations.length;
    if (Array.isArray(c?.connectors) && c.connectors.length) return c.connectors.length;
    return 1;
  };


  // Debug charger data structure for power extraction
  debugChargerPowerData(charger);

  const networkIcon = getNetworkIconPath(getNetworkName(charger));
  const stationCount = getStationCount(charger);
  const availability = getChargerAvailability(charger);
  // Normalized connector extraction (shared logic)
  const getConnectorTypes = React.useCallback((c) => {
    const groups = [];
    const stations = c?.webappData?.[0]?.devices?.[0]?.stations || [];
    const cms = c?.connectors || [];
    let rawList = [];
    if (stations.length) rawList = rawList.concat(stations);
    if (cms.length) rawList = rawList.concat(cms);
    const normalized = rawList.map((r,i)=>normalizeConnector(r,i));
    const seen = new Set();
    normalized.forEach(nc => {
      if (nc.uniqueKey && seen.has(nc.uniqueKey)) return;
      if (nc.uniqueKey) seen.add(nc.uniqueKey);
      const canonical = nc.baseType; // canonical
      const info = getConnectorInfo(canonical || 'Unknown');
      const powerDisplay = extractConnectorPower(nc.raw, c);
      const powerInfo = getPowerDisplayInfo(powerDisplay.includes('kW') ? powerDisplay.split(' ')[0] : powerDisplay);
      const existing = groups.find(g => g.type === canonical);
      if (existing) {
        existing.count += 1;
        if (powerInfo.value) {
          if (!existing.powerInfo?.value || powerInfo.value > existing.powerInfo.value) {
            existing.powerDisplay = powerDisplay;
            existing.powerInfo = powerInfo;
          }
        }
      } else {
        groups.push({
          type: canonical,
          displayName: info.displayName || canonical,
          icon: info.icon,
          count: 1,
          powerDisplay,
          powerInfo
        });
      }
    });
    if (!normalized.length) {
      // Fallback connector display
      const fallbackDisplay = 'Unknown';
      groups.push({
        type: 'Unknown',
        icon: getConnectorInfo('Unknown').icon,
        count: 1,
        powerDisplay: fallbackDisplay,
        powerInfo: getPowerDisplayInfo(fallbackDisplay)
      });
    }
    return groups;
  }, []);
  const connectorTypes = getConnectorTypes(charger);

  if (!charger || !position) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        key={charger?.id || charger?.chargepoint_id || 'hover-card'}
        initial={{ opacity: 0, scale: 0.8, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 10 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30, duration: 0.15 }}
        style={{
          position: 'fixed',
          left: (position?.x || 0) - 130,
          top: (position?.y || 0) - 270,
          zIndex: 1000,
          pointerEvents: 'auto',
          transform: 'translate(-50%, -100%)',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 1.5,
            width: 260,
            backgroundColor: cardSurface,
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: cardBorder,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -6,
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 10,
              height: 10,
              backgroundColor: cardSurface,
              border: cardBorder,
              borderLeft: 'none',
              borderBottom: 'none',
              borderRadius: '0 2px 0 0',
              zIndex: 1,
            }
          }}
        >
          {/* Content wrapper to handle overflow while keeping pointer visible */}
          <Box sx={{ overflow: 'hidden' }}>
          {/* Header with Network Icon and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={networkIcon}
                alt="Network"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1.5px solid white',
                  boxShadow: 1,
                }}
                onError={(e) => {
                  e.target.src = '/assets/networkFilter/Generic.svg';
                }}
              />
              {availability && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 400 }}
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: availability === 'available' ? 'success.main' : 
                                     availability === 'in_use' ? 'warning.main' : 'error.main',
                      borderRadius: '50%',
                      border: '1.5px solid white',
                      boxShadow: 2,
                      animation: availability === 'available' ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { 
                          transform: 'scale(1)', 
                          boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' 
                        },
                        '70%': { 
                          transform: 'scale(1.1)', 
                          boxShadow: '0 0 0 4px rgba(76, 175, 80, 0)' 
                        },
                        '100%': { 
                          transform: 'scale(1)', 
                          boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' 
                        }
                      }
                    }}
                  />
                </motion.div>
              )}
            </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight="600" noWrap sx={{ fontSize: '0.8rem' }}>
                        {charger.chargepoint_name || charger.name || 'Charging Station'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {charger.team_name || charger.operator || 'Network'}
                      </Typography>
                    </Box>
            {onToggleFavorite && (
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(charger, !isFavorite);
                }}
              >
                {isFavorite ? (
                  <FavoriteIcon sx={{ color: 'red', fontSize: 16 }} />
                ) : (
                  <FavoriteBorderIcon sx={{ color: 'grey.400', fontSize: 16 }} />
                )}
              </IconButton>
            )}
          </Box>

                  {/* Station Name and Address */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block', fontSize: '0.65rem' }}>
                      ID: {charger.chargepoint_id || charger.id || 'N/A'}
                    </Typography>
                    {/* Enhanced address display with multiple field support */}
                    {(() => {
                      const addressParts = [];
                      
                      // Try different address field combinations
                      const possibleAddresses = [
                        charger.fullAddress,
                        charger.address,
                        charger.location?.address,
                        charger.street,
                        charger.locationStreet
                      ].filter(Boolean);
                      
                      if (possibleAddresses.length > 0) {
                        // Use the first available full address
                        addressParts.push(possibleAddresses[0]);
                      } else {
                        // Build address from parts
                        const street = charger.street || charger.locationStreet;
                        if (street) addressParts.push(street);
                        
                        // City, State, Postal Code
                        const cityStateParts = [];
                        const city = charger.city || charger.locationCity;
                        const state = charger.state || charger.locationState;
                        const postal = charger.postalCode || charger.locationPostalCode || charger.zipCode;
                        
                        if (city) cityStateParts.push(city);
                        if (state) cityStateParts.push(state);
                        if (postal) cityStateParts.push(postal);
                        
                        if (cityStateParts.length > 0) {
                          addressParts.push(cityStateParts.join(', '));
                        }
                      }
                      
                      // Add country if available and not already included
                      const country = charger.country || charger.locationCountry;
                      if (country && !addressParts.some(part => part.toLowerCase().includes(country.toLowerCase()))) {
                        addressParts.push(country);
                      }
                      
                      // If no address found, try coordinates with reverse geocoding fallback
                      if (addressParts.length === 0) {
                        const lat = parseFloat(charger.locationLatitude || charger.lat);
                        const lng = parseFloat(charger.locationLongitude || charger.lng);
                        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                          addressParts.push(`Near ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
                        }
                      }
                      
                      return addressParts.length > 0 ? (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.65rem', 
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {addressParts.join(' • ')}
                        </Typography>
                      ) : (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.65rem', 
                            fontStyle: 'italic',
                            lineHeight: 1.3
                          }}
                        >
                          Location information not available
                        </Typography>
                      );
                    })()}
                  </Box>

          {/* Station Count and Status */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: 1, 
            p: 1, 
            backgroundColor: metaSurface, 
            borderRadius: 1.5,
            border: isDark
              ? `1px solid ${alpha(theme.palette.common.white, 0.08)}`
              : '1px solid rgba(0, 0, 0, 0.04)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BoltIcon sx={{ color: theme.palette.success.main, fontSize: 14 }} />
              <Typography variant="caption" fontWeight="600" sx={{ fontSize: '0.7rem' }}>
                {stationCount} Station{stationCount > 1 ? 's' : ''}
              </Typography>
            </Box>
            {availability && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 500 }}
              >
                <Chip 
                  label={availability === 'available' ? 'Available' : 
                         availability === 'in_use' ? 'In Use' : 'Unavailable'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    backgroundColor: availability === 'available'
                      ? alpha(theme.palette.success.main, 0.18)
                      : availability === 'in_use'
                        ? alpha(theme.palette.warning.main, 0.18)
                        : alpha(theme.palette.error.main, 0.18),
                    color: availability === 'available' ? 'success.main' : 
                           availability === 'in_use' ? 'warning.main' : 'error.main',
                    border: `1px solid ${availability === 'available'
                      ? alpha(theme.palette.success.main, 0.35)
                      : availability === 'in_use'
                        ? alpha(theme.palette.warning.main, 0.35)
                        : alpha(theme.palette.error.main, 0.35)}`,
                    '& .MuiChip-label': {
                      px: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    },
                    '&::before': availability === 'available' ? {
                      content: '""',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'success.main',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                        '100%': { opacity: 1, transform: 'scale(1)' }
                      }
                    } : {}
                  }}
                />
              </motion.div>
            )}
          </Box>

                  {/* Connector Types */}
                  {connectorTypes.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight="500" sx={{ mb: 0.5, display: 'block', fontSize: '0.65rem' }}>
                        Connector Types:
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: 1,
                        justifyContent: 'flex-start'
                      }}>
                        {connectorTypes.slice(0, 4).map((connector, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              position: 'relative',
                              minWidth: 'auto'
                            }}
                          >
                            {/* Connector Icon with circular background */}
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: connectorBg,
                                ...(isDark
                                  ? {
                                      backgroundImage: viewButtonGradient,
                                      border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
                                      boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.6)}`
                                    }
                                  : {
                                      border: `2px solid ${connectorBorderColor}`,
                                      boxShadow: `inset 0 1px 2px ${alpha(theme.palette.common.white, 0.8)}`
                                    }),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                            >
                              <Box
                                component="img"
                                src={connector.icon}
                                alt={connector.type}
                                sx={{
                                  width: 18,
                                  height: 18,
                                  filter: isDark ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.6)) brightness(1.1)' : 'none'
                                }}
                                onError={(e) => {
                                  e.target.src = '/assets/connectorSvg/img_connector_1.svg';
                                }}
                              />
                              
                              {/* Count badge */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: -4,
                                  right: -4,
                                  minWidth: 18,
                                  height: 18,
                                  borderRadius: '9px',
                                  backgroundImage: viewButtonGradient,
                                  color: theme.palette.common.white,
                                  fontSize: '0.65rem',
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 0.5,
                                  boxShadow: `0 4px 10px ${alpha(theme.palette.common.black, 0.5)}`,
                                  border: 'none'
                                }}
                              >
                                {connector.count}x
                              </Box>
                            </Box>
                          </Box>
                        ))}
                        {connectorTypes.length > 4 && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              backgroundColor: connectorBg,
                              ...(isDark
                                ? {
                                    backgroundImage: viewButtonGradient,
                                    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                                    boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.6)}`
                                  }
                                : {
                                    border: `1px solid ${connectorBorderColor}`,
                                    boxShadow: `inset 0 1px 2px ${alpha(theme.palette.common.white, 0.8)}`
                                  }),
                              borderRadius: '50%'
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight="700"
                              sx={{
                                fontSize: '0.6rem',
                                color: isDark ? theme.palette.common.white : theme.palette.primary.main
                              }}
                            >
                              +{connectorTypes.length - 4}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Only show View on Map button when NOT routing */}
            {onViewOnMap && !isRouting && (
              <Button
                variant="contained"
                size="small"
                startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                onClick={() => onViewOnMap(charger)}
                sx={{
                  flex: 1,
                  backgroundImage: viewButtonGradient,
                  backgroundColor: 'transparent',
                  color: theme.palette.common.white,
                  '&:hover': {
                    backgroundImage: viewButtonGradient,
                    filter: 'brightness(1.08)'
                  },
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  py: 0.5,
                  boxShadow: isDark
                    ? `0 12px 24px ${alpha(theme.palette.common.black, 0.45)}`
                    : `0 12px 24px ${alpha(theme.palette.primary.dark, 0.35)}`,
                  border: 'none'
                }}
              >
                View in Google Map
              </Button>
            )}
            
            {/* Trip Management Button - Only show when routing */}
            {isRouting && (
              isInRoute ? (
                onRemoveFromTrip && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<RemoveIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      onRemoveFromTrip(charger);
                    }}
                    sx={{
                      flex: 1,
                      backgroundColor: theme.palette.error.main,
                      '&:hover': { backgroundColor: theme.palette.error.dark },
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      py: 0.5,
                    }}
                  >
                    ➖ Remove
                  </Button>
                )
              ) : (
                onAddToTrip && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      if (onAddToTrip) {
                        onAddToTrip(charger);
                      } else {
                      }
                    }}
                    sx={{
                      flex: 1,
                      backgroundColor: theme.palette.success.main,
                      '&:hover': { backgroundColor: theme.palette.success.dark },
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      py: 0.5,
                    }}
                  >
                    ➕ Add to Trip
                  </Button>
                )
              )
            )}
          </Box>
          </Box> {/* Close content wrapper */}
        </Paper>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChargerHoverCard;
